import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Avatar,
  Grid,
  Zoom,
  Tooltip,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  LinearProgress,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import test from "../../assets/images/test.png";
import study from "../../assets/images/study.png";
import exam from "../../assets/images/exam.png";
import examTime from "../../assets/images/exam-time.png";
import interviewIcon from "../../assets/images/mental.png";
import school from "../../assets/images/school.png";
import TopPerformers from "./TopPerformers";
import UpcomingProctoredAssessments from "./UpcomingProctoredAssessments";
import { TopPerformersSkeleton } from "./DashboardCardSkeletons";
import { TableNodata } from "../../components/TableNodata";
import { TruncatedText } from "../../components/TruncatedText";
import { useChangeableItems, getLabelByKey, getPluralLabelByKey } from "../../store/useDynamicLabel";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";


// Same card look as InstituteAdminDashboard
export const cardSx = {
  borderRadius: "8px",
  border: "1px solid #c1c1c6ff",
  background: "#fff",
  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
};

const kpiCardSx = {
  ...cardSx,
  flex: { xs: "1 1 100%", sm: "1 1 calc(40% - 16px)", md: "1 1 calc(21% - 16px)" },
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  p: 2,
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px) scale(1.03)",
    boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
    background: "linear-gradient(145deg, #f9fbff, #ffffff)",
  },
};

export const commonAvatarStyle = {
  bgcolor: "#e8f2ff",
  color: "#1e88e5",
  width: 24,
  height: 24,
  fontWeight: "bold",
  border: "2px solid #bbdefb",
};

// Keyed by the API status (upper case); unknown statuses fall back to grey
const PAPER_STATUS_COLORS = {
  DRAFT: "#9e9e9e",
  SCHEDULED: "#ffa000",
  APPROVED: "#8e24aa",
  READY: "#26a69a",
  OPEN: "#1e88e5",
  CLOSED: "#616161",
  COMPLETED: "#43a047",
  CANCELLED: "#e53935",
};
const paperStatusColor = (status) => PAPER_STATUS_COLORS[String(status).toUpperCase()] || "#bdbdbd";

// "SCHEDULED" -> "Scheduled"
const formatStatus = (status = "") =>
  String(status).toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Keyed by the API status (upper case); unknown statuses fall back to grey
const INTERVIEW_STATUS_COLORS = {
  SCHEDULED: "#1e88e5",
  RESCHEDULED: "#ffa000",
  COMPLETED: "#43a047",
  CANCELLED: "#e53935",
  DECLINED: "#8e24aa",
  CALLED_OFF: "#616161",
};
const interviewStatusColor = (status) => INTERVIEW_STATUS_COLORS[String(status).toUpperCase()] || "#bdbdbd";

// API sends { CANCELLED: 2, SCHEDULED: 2 } -> [{ status, count }]
const toStatusList = (value) =>
  Array.isArray(value)
    ? value
    : Object.entries(value || {}).map(([status, count]) => ({ status, count: Number(count) || 0 }));

// Error toast for a failed dashboard API call: backend message when there is one, else `fallback`.
// A 401 is skipped — the api client is already redirecting to the login page.
export const showApiError = (showSnackbar, error, fallback) => {
  if (error?.response?.status === 401) return;
  const detail = error?.response?.data?.detail;
  showSnackbar("error", error?.response?.data?.message || (typeof detail === "string" && detail) || fallback);
};

const percentOf = (count, total) => (total ? Math.min(100, Math.floor((count / total) * 100)) : 0);

const toPercentLabels = (counts) => {
  const total = counts.reduce((sum, count) => sum + count, 0);
  if (!total) return counts.map(() => "0%");
  const exact = counts.map((count) => (count / total) * 100);
  const percents = exact.map(Math.floor);
  let remaining = 100 - percents.reduce((sum, value) => sum + value, 0);
  exact
    .map((value, index) => ({ index, remainder: value - percents[index] }))
    .sort((a, b) => b.remainder - a.remainder)
    .forEach(({ index }) => {
      if (remaining > 0) {
        percents[index] += 1;
        remaining -= 1;
      }
    });
  return percents.map((percent, index) => (counts[index] > 0 && percent === 0 ? "<1%" : `${percent}%`));
};

export const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();

/* ---------------- Building blocks ---------------- */

export const SectionTitle = ({ icon, title }) => (
  <Grid sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 1 }}>
    {icon}
    <Typography variant="h6" fontWeight={700}>
      {title}
    </Typography>
  </Grid>
);

export const CardTitle = ({ title, meta }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "18px" }}>
      {title}
    </Typography>
    {meta &&
      (typeof meta === "string" ? (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {meta}
        </Typography>
      ) : (
        meta
      ))}
  </Box>
);

export const KpiCard = ({ label, value, caption, image, onClick, show, delay, loading }) => (
  <Zoom in={show} style={{ transitionDelay: `${delay}ms` }}>
    <Card sx={{ ...kpiCardSx, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#333", lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Avatar sx={commonAvatarStyle}>
          <Box component="img" src={image} alt={label} sx={{ width: 26, height: 26 }} />
        </Avatar>
      </Box>
      <Typography variant="h5" fontWeight={700} sx={{ color: "#1e88e5", mt: 1 }}>
        {loading ? <Skeleton width={60} /> : Number(value || 0).toLocaleString()}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {loading ? <Skeleton width="80%" /> : caption}
      </Typography>
    </Card>
  </Zoom>
);

const LegendItem = ({ color, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: color, flexShrink: 0 }} />
    <Typography variant="body2" sx={{ color: "#555", flex: 1 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} sx={{ color: "#333" }}>
      {value}
    </Typography>
  </Box>
);

/* ---------------- Sections ---------------- */

const TestPapersByStatus = ({ data, title, period, onViewAll, viewAllLabel, loading }) => {
  const counts = data.map((d) => Number(d.count) || 0);
  const total = counts.reduce((sum, count) => sum + count, 0);
  const percentLabels = toPercentLabels(counts);
  return (
    <Card sx={{ ...cardSx, p: 2.5, flex: { xs: "1 1 100%", md: "1.4 1 0" }, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <CardTitle title={title} meta={loading ? "" : [`${total} Total`, period].filter(Boolean).join(" · ")} />
      {loading ? (
        <>
          <Skeleton variant="rounded" height={14} sx={{ mb: 3 }} />
          <Skeleton width="70%" />
          <Skeleton width="50%" />
        </>
      ) : total === 0 ? (
        <TableNodata />
      ) : (
        <>
          <Box sx={{ display: "flex", height: 14, borderRadius: "7px", overflow: "hidden", mb: 3 }}>
            {data.map((d, index) =>
              counts[index] > 0 ? (
                <Tooltip
                  key={d.status}
                  arrow
                  placement="top"
                  title={`${formatStatus(d.status)} · ${counts[index]} (${percentLabels[index]})`}
                >
                  <Box sx={{ flex: counts[index], backgroundColor: paperStatusColor(d.status) }} />
                </Tooltip>
              ) : null
            )}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, rowGap: 1.5, columnGap: 3 }}>
            {data.map((d) => (
              <LegendItem key={d.status} color={paperStatusColor(d.status)} label={formatStatus(d.status)} value={d.count} />
            ))}
          </Box>
        </>
      )}
      <Box sx={{ mt: "auto", pt: 2 }}>
        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onViewAll} sx={{ textTransform: "none", fontWeight: 600, px: 0 }}>
          {viewAllLabel}
        </Button>
      </Box>
    </Card>
  );
};

export const InterviewsByStatus = ({ data, period, loading, headerAction, large }) => {
  const chartSize = large ? 260 : 180;
  const counts = data.map((d) => Number(d.count) || 0);
  const total = counts.reduce((sum, count) => sum + count, 0);
  const percentLabels = toPercentLabels(counts);
  const options = {
    chart: { type: "donut" },
    labels: data.map((d) => formatStatus(d.status)),
    colors: data.map((d) => interviewStatusColor(d.status)),
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: { show: true, label: "Interviews", formatter: () => `${total}` },
          },
        },
      },
    },
    tooltip: {
      y: { formatter: (value, { seriesIndex }) => `${value} interviews (${percentLabels[seriesIndex]})` },
    },
  };
  return (
    <Card
      sx={{
        ...cardSx,
        p: 2.5,
        flex: { xs: "1 1 100%", md: "1 1 0" },
        minWidth: 0,
        ...(large ? { display: "flex", flexDirection: "column" } : {}),
      }}
    >
      <CardTitle title="Interviews by Status" meta={headerAction || period} />
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="circular" width={150} height={150} sx={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="80%" />
            <Skeleton width="60%" />
          </Box>
        </Box>
      ) : total === 0 ? (
        <TableNodata />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: large ? 4 : 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            ...(large ? { flex: 1, justifyContent: "center" } : {}),
          }}
        >
          <Box sx={{ width: chartSize, flexShrink: 0, mx: { xs: "auto", sm: 0 } }}>
            <Chart options={options} series={counts} type="donut" width={chartSize} />
          </Box>
          <Box sx={{ flex: large ? "0 1 220px" : 1, minWidth: 160, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {data.map((d) => (
              <LegendItem key={d.status} color={interviewStatusColor(d.status)} label={formatStatus(d.status)} value={d.count} />
            ))}
          </Box>
        </Box>
      )}
    </Card>
  );
};

// Chip colour per interview status
const interviewChipColor = (status = "") =>
  ({ RESCHEDULED: "warning", COMPLETED: "success", CANCELLED: "error", DECLINED: "error", CALLED_OFF: "default" })[String(status).toUpperCase()] || "primary";

// Same list height as Top Performers / Upcoming Assessments
const INTERVIEW_LIST_HEIGHT = 358;

// /upcoming-interviews: next 30 days, soonest first (same look as Upcoming Assessments)
export const UpcomingInterviews = ({
  data,
  loading,
  onOpenScheduler,
  onOpenInterview,
  title = "Upcoming Interviews",
  emptyMessage = "No Upcoming Interviews",
  emptyDescription = "No interviews scheduled in the next 30 days.",
  footerLabel = "Open Interview Scheduler",
  listMinHeight,
  showCompany,
}) => (
  // Stretches to the row height (= the other two cards) on wide screens
  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
    <Grid
      container
      direction="column"
      sx={{ ...cardSx, p: 2, width: "100%", minWidth: 0, overflow: "hidden", flex: 1, flexWrap: "nowrap" }}
    >
      {/* Header */}
      <Grid sx={{ display: "flex", gap: 1, mb: 1, minHeight: 40, alignItems: "center" }}>
        <Box component="img" src={interviewIcon} alt={title} sx={{ width: 30, height: 30, flexShrink: 0 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          {title}
        </Typography>
      </Grid>

      {/* List */}
      <Box
        component="ul"
        sx={{
          width: "100%",
          minWidth: 0,
          listStyle: "none",
          pl: 0,
          m: 0,
          // Stacked (small screens): fixed height like the other cards.
          // Side by side (lg): fills the space left under the header and above the button, so the
          // card is exactly as tall as Top Performers / Upcoming Assessments; extra rows scroll.
          height: { xs: INTERVIEW_LIST_HEIGHT, lg: 0 },
          flex: { lg: "1 1 0" },
          ...(listMinHeight ? { minHeight: { lg: listMinHeight } } : {}),
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {loading ? (
          <TopPerformersSkeleton />
        ) : data.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <TableNodata message={emptyMessage} description={emptyDescription} />
          </Box>
        ) : (
          data.map((item) => {
            const scheduledAt = dayjs(item.scheduled_at);
            const roleAndInterviewer = [
              item.job_role,
              item.interviewer_name ? `Int: ${item.interviewer_name}` : null,
            ].filter(Boolean).join(" · ");
            return (
              <Box
                component="li"
                key={`interview-${item.interview_id}`}
                onClick={() => onOpenInterview(item)}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  mb: 1.5,
                  borderBottom: "1px solid #f2f2f2",
                  pb: 1,
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f9fafc", borderRadius: 2, pl: 1 },
                }}
              >
                <Avatar sx={{ ...commonAvatarStyle, width: 32, height: 32, fontSize: 12, mt: 0.25, flexShrink: 0 }}>
                  {initials(item.candidate_name)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Candidate + date pill */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Tooltip title={item.candidate_email || ""} placement="top-start" arrow>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: "14px", color: "text.primary", minWidth: 0 }}>
                        {item.candidate_name}
                      </Typography>
                    </Tooltip>
                    {scheduledAt.isValid() && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.25,
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          backgroundColor: "#e3f2fd",
                          color: "#1565c0",
                        }}
                      >
                        {scheduledAt.format("MMMM D")}
                      </Box>
                    )}
                  </Box>

                  {showCompany && item.institute_name && (
                    <TruncatedText onlyWhenTruncated text={item.institute_name} sx={{ fontSize: "12px", color: "text.secondary" }} />
                  )}

                  {/* Job role · interviewer */}
                  {roleAndInterviewer && (
                    <TruncatedText
                      onlyWhenTruncated
                      text={roleAndInterviewer}
                      sx={{ fontSize: "12px", color: "#1e88e5", fontWeight: 500 }}
                    />
                  )}

                  {/* Time · duration + status */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 0.25 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }} noWrap>
                      {scheduledAt.isValid() && (
                        <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                          {scheduledAt.format("hh:mm A")}
                        </Box>
                      )}
                      {item.duration_minutes ? ` · ${item.duration_minutes} Minutes` : ""}
                    </Typography>
                    <Chip
                      label={formatStatus(item.status)}
                      size="small"
                      color={interviewChipColor(item.status)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: "11px", fontWeight: 600, flexShrink: 0 }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <Box sx={{ pt: 1 }}>
        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onOpenScheduler} sx={{ textTransform: "none", fontWeight: 600, px: 0 }}>
          {footerLabel}
        </Button>
      </Box>
    </Grid>
  </Box>
);

// Tile width: 3 per row on large screens, 2 on tablets, 1 on phones (16px gap)
const progressTileWidth = { xs: "100%", sm: "calc((100% - 16px) / 2)", lg: "calc((100% - 32px) / 3)" };

// Same arrow look as the dashboard table pagination
const arrowButtonSx = {
  border: "1px solid #d5d7da",
  borderRadius: "8px",
  width: 32,
  height: 32,
  "&.Mui-disabled": { borderColor: "#ececec" },
};

// /open-assessments (Hiring Manager): completed / assigned candidates for each open assessment.
// One sliding row; the arrows in the header move it by one screen of tiles.
const AssessmentProgress = ({ data, loading, title, onOpen }) => {
  const rowRef = useRef(null);
  const [canBack, setCanBack] = useState(false);
  const [canForward, setCanForward] = useState(false);

  // Enable/disable the arrows from the row's scroll position
  const updateArrows = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    setCanBack(row.scrollLeft > 0);
    setCanForward(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [data, loading, updateArrows]);

  const slide = (direction) => {
    const row = rowRef.current;
    if (row) row.scrollBy({ left: direction * row.clientWidth, behavior: "smooth" });
  };

  return (
    <Card sx={{ ...cardSx, p: 2.5, mb: 3 }}>
      {/* Title · open count · arrows */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "18px" }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!loading && data.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", mr: 0.5 }}>
              {data.length} Open
            </Typography>
          )}
          <IconButton size="small" onClick={() => slide(-1)} disabled={!canBack} sx={arrowButtonSx} aria-label="Previous">
            <ArrowBackIcon sx={{ fontSize: 18, color: canBack ? "#414651" : "#a2a2a2" }} />
          </IconButton>
          <IconButton size="small" onClick={() => slide(1)} disabled={!canForward} sx={arrowButtonSx} aria-label="Next">
            <ArrowForwardIcon sx={{ fontSize: 18, color: canForward ? "#414651" : "#a2a2a2" }} />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
          {[0, 1, 2].map((tile) => (
            <Skeleton key={tile} variant="rounded" height={96} sx={{ flex: "0 0 auto", width: progressTileWidth }} />
          ))}
        </Box>
      ) : data.length === 0 ? (
        <TableNodata />
      ) : (
        <Box
          ref={rowRef}
          onScroll={updateArrows}
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {data.map((item) => {
            const assigned = Number(item.assigned_students_count) || 0;
            const completed = Number(item.completed_students_count) || 0;
            const progress = percentOf(completed, assigned);
            const scheduledAt = dayjs(`${item.scheduled_date}T${item.scheduled_time || "00:00:00"}`);
            return (
              <Box
                key={item.assessment_id}
                onClick={() => onOpen(item)}
                sx={{
                  flex: "0 0 auto",
                  width: progressTileWidth,
                  boxSizing: "border-box",
                  scrollSnapAlign: "start",
                  border: "1px solid #eceff4",
                  borderRadius: "8px",
                  p: 1.5,
                  minWidth: 0,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: "#f9fafc", boxShadow: "0px 2px 8px rgba(0,0,0,0.08)" },
                }}
              >
                {/* Assessment name + progress */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                  <TruncatedText onlyWhenTruncated text={item.assessment_name} sx={{ fontWeight: 600, fontSize: "14px" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#1e88e5", flexShrink: 0 }}>
                    {progress}%
                  </Typography>
                </Box>
                {/* Job role */}
                {item.program_name && (
                  <TruncatedText onlyWhenTruncated text={item.program_name} sx={{ fontSize: "12px", color: "#1e88e5", fontWeight: 500 }} />
                )}
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{ height: 8, borderRadius: 4, my: 1, backgroundColor: "#e3f2fd", "& .MuiLinearProgress-bar": { borderRadius: 4 } }}
                />
                {/* Completed / assigned + schedule */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }} noWrap>
                    <Box component="span" sx={{ fontWeight: "bold", color: "text.primary" }}>
                      {completed}/{assigned}
                    </Box>
                    {" completed"}
                  </Typography>
                  {scheduledAt.isValid() && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px", flexShrink: 0 }}>
                      {scheduledAt.format("MMM D, hh:mm A")}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Card>
  );
};

// Page header: title + month filter (last 12 months)
export const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => dayjs().startOf("month").subtract(i, "month"));

export const DashboardHeader = ({ selectedMonth, onMonthChange, title = "Dashboard", subtitle, children }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <DashboardIcon sx={{ color: "#1e88e5", fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700} sx={{ color: "#0a0a0a" }}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {(children || onMonthChange) && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        {children}
        {onMonthChange && (
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Month</InputLabel>
            <Select label="Month" value={selectedMonth} onChange={(e) => onMonthChange(e.target.value)}>
              {MONTH_OPTIONS.map((month) => (
                <MenuItem key={month.format("YYYY-MM")} value={month.format("YYYY-MM")}>
                  {month.format("MMMM YYYY")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    )}
  </Box>
);

/* ---------------- Data hooks ---------------- */

// GET /dashboard/summary for the selected month ("YYYY-MM"); the backend scopes it by the login role.
export const useDashboardSummary = (selectedMonth, instituteId = null) => {
  const { showSnackbar } = useSnackbar();
  const [summary, setSummary] = useState({});
  const [testPapersByStatus, setTestPapersByStatus] = useState([]);
  const [interviewsByStatus, setInterviewsByStatus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Ignore a response that arrives after the month has changed again
    let ignore = false;
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const month = selectedMonth ? dayjs(`${selectedMonth}-01`) : null;
        const res = await api.get("/dashboard/summary", {
          params: {
            ...(instituteId ? { institute_id: instituteId } : {}),
            ...(month ? { year: month.year(), month: month.month() + 1 } : {}),
          },
        });
        if (ignore) return;
        const isSuccess = res?.data?.status === "success";
        setSummary(isSuccess ? res.data.data?.summary || {} : {});
        setTestPapersByStatus(isSuccess ? res.data.data?.test_papers_by_status || [] : []);
        setInterviewsByStatus(isSuccess ? toStatusList(res.data.data?.summary?.interviews_by_status) : []);
      } catch (error) {
        if (!ignore) {
          setSummary({});
          setTestPapersByStatus([]);
          setInterviewsByStatus([]);
          showApiError(showSnackbar, error, "Failed to load the dashboard summary. Please try again.");
        }
        console.log("Error : ", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchSummary();
    return () => {
      ignore = true;
    };
  }, [selectedMonth, instituteId, showSnackbar]);

  return { summary, testPapersByStatus, interviewsByStatus, loading };
};

// GET /upcoming-interviews: next 30 days, soonest first (not tied to the month filter); skipped when `enabled` is false
export const useUpcomingInterviews = (enabled = true, instituteId = null) => {
  const { showSnackbar } = useSnackbar();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let ignore = false;
    const fetchUpcomingInterviews = async () => {
      setLoading(true);
      try {
        const res = await api.get("/upcoming-interviews", { params: instituteId ? { institute_id: instituteId } : {} });
        if (ignore) return;
        const list = res?.data?.status === "success" ? res.data.data?.interviews || [] : [];
        setInterviews([...list].sort((x, y) => dayjs(x.scheduled_at).diff(dayjs(y.scheduled_at))));
      } catch (error) {
        if (!ignore) {
          setInterviews([]);
          showApiError(showSnackbar, error, "Failed to load upcoming interviews. Please try again.");
        }
        console.log("Error : ", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchUpcomingInterviews();
    return () => {
      ignore = true;
    };
  }, [enabled, instituteId, showSnackbar]);

  return { interviews, loading };
};

// GET /open-assessments (Hiring Manager only; skipped when `enabled` is false)
const useOpenAssessments = (enabled) => {
  const { showSnackbar } = useSnackbar();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let ignore = false;
    const fetchOpenAssessments = async () => {
      setLoading(true);
      try {
        const res = await api.get("/open-assessments");
        if (ignore) return;
        setAssessments(res?.data?.status === "success" ? res.data.data?.assessments || [] : []);
      } catch (error) {
        if (!ignore) {
          setAssessments([]);
          showApiError(showSnackbar, error, "Failed to load assessment progress. Please try again.");
        }
        console.log("Error : ", error);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchOpenAssessments();
    return () => {
      ignore = true;
    };
  }, [enabled, showSnackbar]);

  return { assessments, loading };
};

const useInstituteNames = (enabled) => {
  const { showSnackbar } = useSnackbar();
  const [institutes, setInstitutes] = useState([]);

  useEffect(() => {
    if (!enabled) return;
    let ignore = false;
    const fetchInstitutes = async () => {
      try {
        const res = await api.post("/ListInstituteNames");
        if (!ignore && res?.data?.status === "success") setInstitutes(res.data.data || []);
      } catch (error) {
        if (!ignore) showApiError(showSnackbar, error, "Failed to load the company list. Please try again.");
        console.log("Error : ", error);
      }
    };
    fetchInstitutes();
    return () => {
      ignore = true;
    };
  }, [enabled, showSnackbar]);

  return institutes;
};

/* ---------------- Page ---------------- */

// Corporate dashboard: HR Admin (INSTITUTE_ADMIN), Hiring Manager (TRAINER) and Super Admin (corporate selected).
// The backend scopes every API by the login token; the Hiring Manager also gets the Assessment Progress card.
const CorporateDashboard = () => {
  const navigate = useNavigate();
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);
  const role = localStorage.getItem("role");
  const isHiringManager = role === "TRAINER";
  const isSuperAdmin = role === "SUPER_ADMIN";
  const myLabelPrefix = isHiringManager ? "My " : "";

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Month filter (sent to /dashboard/summary)
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].format("YYYY-MM"));
  const periodLabel = dayjs(`${selectedMonth}-01`).format("MMM YYYY");

  const [selectedInstituteId, setSelectedInstituteId] = useState(null);
  const instituteId = isSuperAdmin && selectedInstituteId ? selectedInstituteId : null;

  const { summary, testPapersByStatus, interviewsByStatus, loading: summaryLoading } = useDashboardSummary(selectedMonth, instituteId);
  const { interviews: upcomingInterviews, loading: interviewsLoading } = useUpcomingInterviews(true, instituteId);
  const { assessments: openAssessments, loading: openAssessmentsLoading } = useOpenAssessments(isHiringManager);
  const institutes = useInstituteNames(isSuperAdmin);
  const companyOptions = [{ institute_id: null, name: `All ${getPluralLabelByKey(changeableItems, "INSTITUTE")}` }, ...institutes];

  const assessmentsThisPeriod = summary.assessments_this_period || {};
  const candidates = summary.candidates || {};
  const organizations = summary.organizations || {};
  const testPapers = summary.test_papers || {};
  const drives = summary.drives || {};

  return (
    <Box>
      <DashboardHeader selectedMonth={selectedMonth} onMonthChange={setSelectedMonth}>
        {isSuperAdmin && (
          <Autocomplete
            size="small"
            sx={{ width: 240 }}
            options={companyOptions}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.institute_id === value.institute_id}
            value={companyOptions.find((option) => option.institute_id === selectedInstituteId) || companyOptions[0]}
            onChange={(event, newValue) => setSelectedInstituteId(newValue?.institute_id ?? null)}
            disableClearable
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <TruncatedText text={option.name} sx={{ width: "100%" }} />
                </li>
              );
            }}
            renderInput={(params) => <TextField {...params} label={getLabel("INSTITUTE")} placeholder="Search" />}
          />
        )}
      </DashboardHeader>

      {/* KPI cards */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "stretch", flexWrap: "wrap", gap: 1, mb: 3 }}>
        {isSuperAdmin ? (
          <>
            <KpiCard
              show={loaded}
              delay={300}
              loading={summaryLoading}
              label={getPluralLabelByKey(changeableItems, "INSTITUTE")}
              value={organizations.total}
              caption={`${organizations.active ?? 0} active`}
              image={school}
              onClick={() => navigate("/institutes")}
            />
            <KpiCard
              show={loaded}
              delay={400}
              loading={summaryLoading}
              label={`${getLabel("QUESTION_PAPER")}s`}
              value={testPapers.total}
              caption={`Self ${testPapers.self ?? 0} · Proctored ${testPapers.proctored ?? 0}`}
              image={test}
              onClick={() => navigate("/questionPaperBuilder")}
            />
            <KpiCard
              show={loaded}
              delay={500}
              loading={summaryLoading}
              label={`${getLabel("STUDENT")}s`}
              value={candidates.total}
              caption={`Across all ${getPluralLabelByKey(changeableItems, "BATCHES").toLowerCase()}`}
              image={study}
              onClick={() => navigate("/students")}
            />
            <KpiCard
              show={loaded}
              delay={600}
              loading={summaryLoading}
              label={getPluralLabelByKey(changeableItems, "BATCHES")}
              value={drives.total}
              caption={`${drives.active ?? 0} active`}
              image={exam}
              onClick={() => navigate("/batches")}
            />
          </>
        ) : (
          <>
            <KpiCard
              show={loaded}
              delay={300}
              loading={summaryLoading}
              label={`${getLabel("EXAM")}s this month`}
              value={assessmentsThisPeriod.total}
              caption={`Self ${assessmentsThisPeriod.self ?? 0} · Proctored ${assessmentsThisPeriod.proctored ?? 0}`}
              image={test}
              onClick={() => navigate("/questionPaperBuilder")}
            />
            <KpiCard
              show={loaded}
              delay={400}
              loading={summaryLoading}
              label={`${myLabelPrefix}${getLabel("STUDENT")}s`}
              value={candidates.count}
              caption={`Across ${candidates.drives ?? 0} ${getLabel("BATCHES").toLowerCase()}`}
              image={study}
              onClick={() => navigate("/students")}
            />
            <KpiCard
              show={loaded}
              delay={500}
              loading={summaryLoading}
              label={`${getLabel("EXAM")}s completed`}
              value={summary.assessments_completed}
              caption={`In ${periodLabel}`}
              image={exam}
              onClick={() => navigate("/examHistory")}
            />
            <KpiCard
              show={loaded}
              delay={600}
              loading={summaryLoading}
              label="Interviews scheduled"
              value={summary.interviews_scheduled_this_month}
              caption={`In ${periodLabel}`}
              image={examTime}
              onClick={() => navigate("/interview-scheduler-list")}
            />
          </>
        )}
      </Box>

      {/* Test papers + interviews by status */}
      <SectionTitle
        icon={<AssignmentOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />}
        title={`${getLabel("QUESTION_PAPER")} & Interview Overview`}
      />
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, mb: 3 }}>
        <TestPapersByStatus
          data={testPapersByStatus}
          loading={summaryLoading}
          title={`${myLabelPrefix}${getLabel("QUESTION_PAPER")}s by Status`}
          period={periodLabel}
          viewAllLabel={`View all ${getLabel("QUESTION_PAPER").toLowerCase()}s`}
          onViewAll={() => navigate("/questionPaperBuilder")}
        />
        <InterviewsByStatus data={interviewsByStatus} period={periodLabel} loading={summaryLoading} />
      </Box>

      {/* Assessment progress (Hiring Manager: open assessments) */}
      {isHiringManager && (
        <>
          <SectionTitle
            icon={<GroupsOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />}
            title={`${getLabel("EXAM")}s`}
          />
          <AssessmentProgress
            data={openAssessments}
            loading={openAssessmentsLoading}
            title={`${getLabel("EXAM")} Progress`}
            onOpen={(item) => navigate("/questionPaperBuilder/view", { state: { assessmentId: item.assessment_id } })}
          />
        </>
      )}

      {/* Performance: existing components (live data) + upcoming interviews */}
      <SectionTitle
        icon={<SchoolOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />}
        title={`${isHiringManager ? `${getLabel("STUDENT")}s` : getLabel("INSTITUTE")} performance Overview`}
      />
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isSuperAdmin ? <TopPerformers showInstituteFilter instituteId={instituteId} /> : <TopPerformers showTestTypeFilter />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isSuperAdmin ? (
            <UpcomingProctoredAssessments showInstituteFilter instituteId={instituteId} showCounts={false} />
          ) : (
            <UpcomingProctoredAssessments showTestTypeFilter showCounts={false} />
          )}
        </Box>
        <UpcomingInterviews
          data={upcomingInterviews}
          loading={interviewsLoading}
          showCompany={isSuperAdmin}
          onOpenScheduler={() => navigate("/interview-scheduler-list")}
          onOpenInterview={(item) => navigate("/view-interview-scheduler", { state: { addInterviewSchedule: "false", interviewId: item.interview_id } })}
        />
      </Box>
    </Box>
  );
};

export default CorporateDashboard;
